import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getTemplates,
  saveTemplate,
  cloneTemplate,
  archiveTemplate,
  unarchiveTemplate,
  validateTemplate,
  getTemplateByKey,
  createTemplate,
  updateTemplate,
  deleteTemplate,
} from "../../services/notificationTemplateService";

export const fetchTemplatesThunk = createAsyncThunk(
  "templates/fetch",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getTemplates(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch templates"
      );
    }
  }
);

export const saveTemplateThunk = createAsyncThunk(
  "templates/save",
  async (templateData, { rejectWithValue }) => {
    try {
      const response = await saveTemplate(templateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to save template"
      );
    }
  }
);

export const createTemplateThunk = createAsyncThunk(
  "templates/create",
  async (templateData, { rejectWithValue }) => {
    try {
      const response = await createTemplate(templateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create template"
      );
    }
  }
);

export const updateTemplateThunk = createAsyncThunk(
  "templates/update",
  async ({ key, templateData, bumpVersion = false }, { rejectWithValue }) => {
    try {
      const response = await updateTemplate(key, templateData, bumpVersion);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update template"
      );
    }
  }
);

export const cloneTemplateThunk = createAsyncThunk(
  "templates/clone",
  async ({ key, templateData, locale }, { rejectWithValue }) => {
    try {
      // Pass the full template data in the body and locale as a query param
      const response = await cloneTemplate(key, templateData, { locale });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to clone template"
      );
    }
  }
);

export const archiveTemplateThunk = createAsyncThunk(
  "templates/archive",
  async ({ key, version }, { rejectWithValue }) => {
    try {
      const response = await archiveTemplate(key, version);
      return { ...response.data, archived: true, key, version };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to archive template"
      );
    }
  }
);

export const unarchiveTemplateThunk = createAsyncThunk(
  "templates/unarchive",
  async ({ key, version }, { rejectWithValue }) => {
    try {
      const response = await unarchiveTemplate(key, version);
      return { ...response.data, archived: false, key, version };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to unarchive template"
      );
    }
  }
);

export const deleteTemplateThunk = createAsyncThunk(
  "templates/delete",
  async ({ key, version, locale, force = false }, { rejectWithValue }) => {
    try {
      const response = await deleteTemplate(key, { version, locale, force });
      return { ...response.data, key, version, locale };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete template"
      );
    }
  }
);

export const validateTemplateThunk = createAsyncThunk(
  "templates/validate",
  async (templateData, { rejectWithValue }) => {
    try {
      const response = await validateTemplate(templateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to validate template"
      );
    }
  }
);

export const fetchTemplateByKeyThunk = createAsyncThunk(
  "templates/fetchByKey",
  async ({ key, locale, version }, { rejectWithValue }) => {
    try {
      const params = {};
      if (locale) params.locale = locale;
      if (version) params.version = version;

      const response = await getTemplateByKey(key, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch template"
      );
    }
  }
);
